import { type Key, useMemo, useState } from 'react';

import {
  Button,
  Combobox,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Text,
  TextField,
} from '@backstage/ui';

import { useCapturePulseBlock, useCreatePulse, usePulses } from '../../hooks';
import {
  PULSE_BLOCK_TYPE,
  type PulseListItem,
  type SaveToPulseInput,
} from '../../types';
import { getPulseTitle, stripPulseVisualizationResultData } from '../../utils';
import { usePulseBlockCaptures } from '../PulseBlockCaptures';
import classes from './SaveToPulseDialog.module.css';

type SaveToPulseDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  input: SaveToPulseInput;
};

type PulseMode = 'existing' | 'new';

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Unknown error';

const PulseLoadingState = () => (
  <Text variant="body-small" color="secondary">
    Loading pulses...
  </Text>
);

export const SaveToPulseDialog = ({
  isOpen,
  onOpenChange,
  input,
}: SaveToPulseDialogProps) => {
  const { pulses, isLoading } = usePulses();
  const { capturePulseBlock, isMutating } = useCapturePulseBlock();
  const { createPulse, isCreating } = useCreatePulse();
  const { addCapture } = usePulseBlockCaptures();

  const [mode, setMode] = useState<PulseMode>('existing');
  const [blockName, setBlockName] = useState(input.suggestedName);
  const [newPulseName, setNewPulseName] = useState('');
  const [createdPulseName, setCreatedPulseName] = useState<string | null>(null);
  const [selectedPulseName, setSelectedPulseName] = useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const pulseOptions = useMemo(
    () =>
      (pulses ?? []).map(pulse => ({
        value: pulse.name,
        label: getPulseTitle(pulse),
      })),
    [pulses],
  );

  const selectedPulse = useMemo<PulseListItem | null>(
    () =>
      (pulses ?? []).find(pulse => pulse.name === selectedPulseName) ?? null,
    [pulses, selectedPulseName],
  );

  const isSaving = isMutating || isCreating;

  const resetAndClose = () => {
    setErrorMessage(null);
    onOpenChange(false);
  };

  const getBlockDisplayName = () => blockName.trim() || input.suggestedName;

  const getCaptureVisualizationSpec = () =>
    input.blockType === PULSE_BLOCK_TYPE.CHART
      ? stripPulseVisualizationResultData(input.visualizationSpec)
      : input.visualizationSpec;

  const handleAddToExistingPulse = async () => {
    if (!selectedPulse) return;
    setErrorMessage(null);
    try {
      const result = await capturePulseBlock({
        sourceChat: input.sourceChat,
        sourceEvent: input.sourceEvent,
        displayName: getBlockDisplayName(),
        blockType: input.blockType,
        visualizationSpec: getCaptureVisualizationSpec(),
        pulse: selectedPulse.name,
      });
      if (result.block?.name) {
        addCapture({
          pulseName: selectedPulse.name,
          pulseDisplayName: selectedPulse.displayName,
          blockName: result.block.name,
          blockDisplayName: result.block.displayName || getBlockDisplayName(),
        });
      }
      resetAndClose();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  const handleCreateAndAdd = async () => {
    const displayName = newPulseName.trim();
    if (!displayName && !createdPulseName) return;

    setErrorMessage(null);
    try {
      const pulseName =
        createdPulseName ??
        (await createPulse({ displayName }, undefined)).name;
      setCreatedPulseName(pulseName);
      const result = await capturePulseBlock({
        sourceChat: input.sourceChat,
        sourceEvent: input.sourceEvent,
        displayName: getBlockDisplayName(),
        blockType: input.blockType,
        visualizationSpec: getCaptureVisualizationSpec(),
        pulse: pulseName,
      });
      if (result.block?.name) {
        addCapture({
          pulseName,
          pulseDisplayName: displayName,
          blockName: result.block.name,
          blockDisplayName: result.block.displayName || getBlockDisplayName(),
        });
      }
      resetAndClose();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  const handleNewPulseNameChange = (value: string) => {
    setNewPulseName(value);
    setCreatedPulseName(null);
  };

  const handlePulseSelectionChange = (key: Key | null) => {
    setSelectedPulseName(key ? String(key) : null);
  };

  const canAddToExistingPulse =
    mode === 'existing' && selectedPulse !== null && !isSaving;
  const canCreateAndAdd =
    mode === 'new' && newPulseName.trim().length > 0 && !isSaving;
  const createAndAddButtonLabel = (() => {
    if (isSaving) return 'Adding...';
    if (createdPulseName) return 'Add to pulse';
    return 'Create and add';
  })();

  const pulseSelector = isLoading ? (
    <PulseLoadingState />
  ) : (
    <Combobox
      label="Choose a pulse"
      options={pulseOptions}
      placeholder="Search pulses"
      selectedKey={selectedPulseName}
      onSelectionChange={handlePulseSelectionChange}
      menuTrigger="focus"
    />
  );

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange}>
      <DialogHeader>Add to pulse</DialogHeader>
      <DialogBody className={classes.body}>
        <div className={classes.form}>
          <TextField
            label="Block name"
            value={blockName}
            onChange={setBlockName}
          />

          <div className={classes.modeActions} aria-label="Pulse save mode">
            <Button
              variant={mode === 'existing' ? 'primary' : 'secondary'}
              size="small"
              onPress={() => setMode('existing')}
            >
              Existing pulse
            </Button>
            <Button
              variant={mode === 'new' ? 'primary' : 'secondary'}
              size="small"
              onPress={() => setMode('new')}
            >
              New pulse
            </Button>
          </div>

          {mode === 'existing' && pulseSelector}

          {mode === 'new' && (
            <TextField
              label="Pulse name"
              value={newPulseName}
              onChange={handleNewPulseNameChange}
              placeholder="e.g. Streaming metrics"
            />
          )}

          {mode === 'existing' && !isLoading && pulseOptions.length === 0 && (
            <Text
              as="p"
              variant="body-small"
              color="secondary"
              className={classes.helpText}
            >
              Create a new pulse to add this block.
            </Text>
          )}

          {errorMessage && (
            <Text
              as="p"
              variant="body-small"
              color="danger"
              className={classes.helpText}
            >
              {errorMessage}
            </Text>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onPress={resetAndClose}>
          Cancel
        </Button>
        {mode === 'existing' ? (
          <Button
            variant="primary"
            onPress={() => void handleAddToExistingPulse()}
            isDisabled={!canAddToExistingPulse}
          >
            {isSaving ? 'Adding...' : 'Add to pulse'}
          </Button>
        ) : (
          <Button
            variant="primary"
            onPress={() => void handleCreateAndAdd()}
            isDisabled={!canCreateAndAdd}
          >
            {createAndAddButtonLabel}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
};
